import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-[#FAF7F2] border border-[#E7DFD5] px-4 py-3 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B]/60";

export const StoryFromJournalDialog = ({ entry, onClose, onSent }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ quote: "", name: "", role: "", consent: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (entry) setForm({ quote: (entry.text || "").slice(0, 600), name: user?.name || "", role: "", consent: false });
  }, [entry, user]);

  if (!entry) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please tick the permission box so we can share your story."); return; }
    if (form.quote.trim().length < 20) { toast.error("Add a few more words — at least 20 characters."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/journal/${entry.id}/story`, form);
      toast.success("Sent to the Story Room. Rich reads every one.");
      onSent?.(entry.id, r.data.story_id);
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send that. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(entry)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border-[#F59E0B]/40 text-[#1C1917] max-w-lg p-6 md:p-8" data-testid="journal-story-dialog">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#B45309] mb-1 flex items-center gap-2"><BookOpen size={12} /> Story Room</div>
        <DialogTitle className="font-display text-3xl tracking-tight leading-none">Turn this note into a story.</DialogTitle>
        <DialogDescription className="text-[#78716C] text-sm">
          Your journal stays private. Only what you send here goes to Rich for review — and nothing is published until you approve the wording and he approves the story.
        </DialogDescription>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <label className="block">
            <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">Your words (edit freely)</span>
            <textarea required minLength={20} maxLength={600} rows={5} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })}
                      className={`${inputCls} resize-none`} data-testid="journal-story-quote" />
            <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A8A29E] mt-1 text-right">{form.quote.length} / 600</div>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">Sign it as</span>
              <input required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="First name, initials, or full name" data-testid="journal-story-name" />
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">What you do (optional)</span>
              <input maxLength={120} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} placeholder="Night nurse, EMT, warehouse lead…" data-testid="journal-story-role" />
            </label>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-[#F59E0B]" data-testid="journal-story-consent" />
            <span className="text-xs text-[#78716C] leading-relaxed">I give T.A.C.C. permission to publish these words on the site, signed the way I chose above. I can ask for it to be removed any time.</span>
          </label>
          <div className="flex flex-wrap gap-3 pt-2">
            <button type="submit" disabled={busy} className="btn-ember text-xs disabled:opacity-50" data-testid="journal-story-submit">
              <Send size={13} /> {busy ? "Sending…" : "Send to the Story Room"}
            </button>
            <button type="button" onClick={onClose} className="btn-outline text-xs" data-testid="journal-story-cancel">Keep it private</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
